import { useState, useEffect, useCallback, useRef } from "react";
import api, { presignedUpload } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Skeleton } from "../components/ui/skeleton";
import { Users, Plus, Pencil, Trash2, CheckCircle2, AlertCircle, Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [qualityResult, setQualityResult] = useState(null);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/agents");
      setAgents(res.data);
    } catch { toast.error("Failed to load agents"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null); setName(""); setFile(null); setPreview(null); setQualityResult(null); setShowDialog(true);
  };
  const openEdit = (a) => {
    setEditing(a); setName(a.name); setFile(null); setPreview(a.image_url || null);
    setQualityResult(a.quality_flags || null); setShowDialog(true);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setQualityResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f || !f.type.startsWith("image/")) { toast.error("Please drop an image file"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setQualityResult(null);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      let res;
      if (file) {
        // Upload file directly to S3 via presigned URL (bypasses API Gateway 10MB limit)
        const { s3_url } = await presignedUpload(file);
        if (editing) {
          res = await api.put(`/agents/${editing.id}/update-from-url`, { name, image_url: s3_url });
        } else {
          res = await api.post("/agents/create-from-url", { name, image_url: s3_url });
        }
        setQualityResult(res.data.quality_flags);
        if (res.data.quality_flags?.passed) {
          toast.success(editing ? "Agent updated" : "Agent created - All quality checks passed!");
        } else {
          toast.warning(editing ? "Agent updated with issues" : "Agent created but has quality issues");
        }
      } else if (editing) {
        await api.put(`/agents/${editing.id}`, { name, image_url: editing.image_url || "" });
        toast.success("Agent name updated");
      } else {
        toast.error("Please upload an avatar image"); setSaving(false); return;
      }
      setShowDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save agent");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/agents/${id}`);
      setAgents(prev => prev.filter(a => a.id !== id));
      toast.success("Agent deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="agents-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Users size={24} className="text-[#4D11F6]" strokeWidth={1.5} />
          <div>
            <h1 className="text-3xl font-bold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>Agents</h1>
            <p className="text-[#676970] text-sm mt-0.5">Manage your virtual presenter avatars</p>
          </div>
        </div>
        <Button onClick={openCreate} data-testid="create-agent-btn"
          className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-10 px-5">
          <Plus size={16} className="mr-2" /> New Agent
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1,2,3].map(i => <div key={i} className="bg-white rounded-xl border border-[#E2E4E7] p-5"><Skeleton className="h-32 w-full rounded-lg mb-3" /><Skeleton className="h-4 w-1/2" /></div>)}
        </div>
      ) : agents.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-center" data-testid="agents-empty">
          <div className="w-14 h-14 rounded-2xl bg-[#4D11F6]/10 flex items-center justify-center mb-4">
            <Users size={24} className="text-[#4D11F6]" />
          </div>
          <h3 className="text-lg font-semibold text-[#020B23] mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>No agents yet</h3>
          <p className="text-[#676970] text-sm mb-5 max-w-xs">Create your first virtual presenter to use in video sessions.</p>
          <Button onClick={openCreate} className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
            <Plus size={16} className="mr-2" /> Create Agent
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {agents.map((agent, idx) => (
            <div key={agent.id} data-testid={`agent-card-${agent.id}`}
              className="bg-white rounded-xl border border-[#E2E4E7] overflow-hidden hover:shadow-md transition-shadow animate-fade-in group"
              style={{ animationDelay: `${idx * 40}ms` }}>
              <div className="h-36 bg-[#F3F3F9] flex items-center justify-center overflow-hidden">
                {agent.image_url ? (
                  <img src={agent.image_url} alt={agent.name} className="w-full h-full object-cover" />
                ) : (
                  <Users size={40} className="text-[#D8D9DD]" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>{agent.name}</h3>
                  <Badge className={agent.status === 'Approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-100 text-xs' : 'bg-amber-50 text-amber-700 border-amber-100 text-xs'}>
                    {agent.status === 'Approved' ? <CheckCircle2 size={11} className="mr-1" /> : <AlertCircle size={11} className="mr-1" />}
                    {agent.status}
                  </Badge>
                </div>
                {/* Quality flags display */}
                {agent.quality_flags && (
                  <div className="space-y-1 mb-2">
                    <div className="flex items-center gap-2 text-xs">
                      {agent.quality_flags.min_resolution_ok ? (
                        <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={10} /> {agent.quality_flags.width}x{agent.quality_flags.height}</span>
                      ) : (
                        <span className="text-amber-600 flex items-center gap-1"><AlertCircle size={10} /> Low res: {agent.quality_flags.width}x{agent.quality_flags.height}</span>
                      )}
                      {agent.quality_flags.face_detected ? (
                        <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={10} /> Face detected</span>
                      ) : (
                        <span className="text-amber-600 flex items-center gap-1"><AlertCircle size={10} /> No face</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="outline" size="sm" onClick={() => openEdit(agent)} className="text-xs border-[#E2E4E7]">
                    <Pencil size={12} className="mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(agent.id)} className="text-xs border-[#E2E4E7] text-[#F5354E] hover:bg-red-50">
                    <Trash2 size={12} className="mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="agent-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Sora, sans-serif' }} className="text-[#020B23]">
              {editing ? "Edit Agent" : "Create Agent"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Agent Name</Label>
              <Input placeholder="e.g., Sarah Miller" value={name} onChange={(e) => setName(e.target.value)}
                data-testid="agent-name-input" className="h-10 bg-white border-[#E2E4E7]" />
            </div>

            {/* File Upload Zone */}
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Avatar Image</Label>
              <div
                onClick={() => fileRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="agent-image-upload"
                className={`relative border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                  preview ? "border-[#4D11F6]/30 bg-[#4D11F6]/5" : "border-[#E2E4E7] hover:border-[#4D11F6]/40 bg-[#F3F3F9]"
                } ${preview ? "p-2" : "p-8"}`}
              >
                {preview ? (
                  <div className="relative">
                    <img src={preview} alt="Preview" className="w-full h-40 object-contain rounded-lg" />
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); setQualityResult(null); }}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload size={28} className="mx-auto text-[#94969C] mb-2" />
                    <p className="text-sm font-medium text-[#1A2238]">Drop image or click to upload</p>
                    <p className="text-xs text-[#94969C] mt-1">Min 512x512, must contain a face</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              </div>
            </div>

            {/* Quality Check Results */}
            {qualityResult && (
              <div className={`rounded-lg p-4 border ${qualityResult.passed ? "bg-emerald-50 border-emerald-100" : "bg-amber-50 border-amber-100"}`}
                data-testid="agent-quality-results">
                <p className={`text-sm font-medium mb-2 ${qualityResult.passed ? "text-emerald-700" : "text-amber-700"}`}>
                  {qualityResult.passed ? "All quality checks passed" : "Quality issues detected"}
                </p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs">
                    {qualityResult.min_resolution_ok ?
                      <CheckCircle2 size={12} className="text-emerald-600" /> :
                      <AlertCircle size={12} className="text-amber-600" />}
                    <span className={qualityResult.min_resolution_ok ? "text-emerald-700" : "text-amber-700"}>
                      Resolution: {qualityResult.width}x{qualityResult.height} {qualityResult.min_resolution_ok ? "(OK)" : "(min 512x512)"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {qualityResult.face_detected ?
                      <CheckCircle2 size={12} className="text-emerald-600" /> :
                      <AlertCircle size={12} className="text-amber-600" />}
                    <span className={qualityResult.face_detected ? "text-emerald-700" : "text-amber-700"}>
                      Face detection: {qualityResult.face_detected ? `${qualityResult.face_count} face(s) found` : "No face detected"}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[#E2E4E7]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !name.trim()} data-testid="save-agent-btn"
              className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
              {saving ? "Uploading & checking..." : editing ? "Update" : "Create Agent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
